import Typography from '@components/v2/ui/Typography/Typography';
import { useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TooltipProps } from './Tooltip.types';
import { useTooltipKeyboard } from './hooks/useTooltipKeyboard';
import { useTooltipPosition } from './hooks/useTooltipPosition';
import { useTooltipVisibility } from './hooks/useTooltipVisibility';
import useStyles from './tooltip.styles';

const Tooltip = ({
  children,
  TooltipContent,
  endAdornment,
  delay = 300,
  transitionDuration = 200,
  placement = 'bottom',
  tabIndex = 0,
  disabled = false,
  fullWidth = false,
  anchorToCursor = false,
  interactive = false,
}: TooltipProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const { classes, cx } = useStyles();

  const {
    visible,
    shouldRender,
    showTooltip,
    hideTooltip,
    toggleTooltip,
    tooltipHandlers,
    changeTooltipVisibility,
  } = useTooltipVisibility({ delay, transitionDuration, interactive });

  const { position: tooltipPosition, trackCursor } = useTooltipPosition({
    shouldRender,
    placement,
    anchorToCursor,
    interactive,
    targetRef,
    tooltipRef,
    changeTooltipVisibility,
  });

  const { handleKeyDown } = useTooltipKeyboard({ interactive, shouldRender, tooltipRef });

  const hasTooltipContent =
    TooltipContent !== null &&
    TooltipContent !== undefined &&
    TooltipContent !== '' &&
    TooltipContent !== false;

  const handleMouseEnter = (event: React.MouseEvent) => {
    trackCursor?.(event);
    showTooltip(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target.matches(':focus-visible')) {
      showTooltip();
    }
  };

  if (disabled || !hasTooltipContent) {
    return <>{children}</>;
  }

  const content =
    typeof TooltipContent === 'string' ? (
      <Typography variant="body2" color="inherit" sx={{ lineHeight: 'normal' }}>
        {TooltipContent}
      </Typography>
    ) : (
      TooltipContent
    );

  return (
    <>
      <div
        ref={targetRef}
        className={cx(classes.tooltipContainer, fullWidth && classes.tooltipContainerFullWidth)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={hideTooltip}
        onMouseMove={trackCursor}
        onFocus={handleFocus}
        onBlur={hideTooltip}
        onKeyDown={handleKeyDown}
        onClickCapture={toggleTooltip}
        aria-describedby={tooltipId}
        tabIndex={tabIndex}
      >
        {children}
      </div>

      {hasTooltipContent &&
        shouldRender &&
        document.body &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={cx(classes.tooltip, {
              [classes.tooltipVisible]: visible,
              [classes.tooltipInteractive]: interactive && visible,
            })}
            style={{
              ...tooltipPosition,
              transitionDuration: `${transitionDuration}ms`,
            }}
            {...tooltipHandlers}
          >
            {endAdornment ? (
              <span className={classes.adornedContent}>
                {content}
                {endAdornment}
              </span>
            ) : (
              content
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
